<template>
  <div>
    <div class="mb-4" v-if="!error">
      <span>
        <v-icon class="mr-2 pb-1">mdi-information</v-icon>
        {{
          $t(
            "components.selectors.brandTreeViewSelector.selectWhichBoatsBrandsAndRangesShouldSeeThisArticle"
          )
        }}
      </span>
    </div>
    <v-alert
      v-if="brands.length === 0 && error"
      border="top"
      type="error"
      color="red"
    >
      {{
        $t(
          "components.selectors.brandTreeViewSelector.selectWhichBoatsBrandsAndRangesShouldSeeThisArticle"
        )
      }}
    </v-alert>
    <div class="mb-3 ml-6" v-if="brandOptions.length > 0">
      <div class="d-flex align-center">
        <v-checkbox
          :input-value="isAllSelected"
          @change="toggleSelectAll"
          color="primary"
          class="mt-0"
          style="width: 30px"
          hide-details
          :ripple="false"
        ></v-checkbox>
        <span class="text--primary">
          {{ $t("components.selectors.brandTreeViewSelector.allBrands") }}
        </span>
      </div>
    </div>
    <v-treeview
      class="mb-6"
      v-model="brands"
      color="primary"
      selected-color="primary"
      selectable
      selection-type="independent"
      :items="brandOptions"
      item-key="uuid"
      :open.sync="openedNodes"
    >
      <template v-slot:label="{ item }">
        <span> {{ item.name }}</span>
        <span class="ml-2">
          <v-chip outlined>{{ getGroupType(item) }}</v-chip>
        </span>
      </template>
    </v-treeview>
  </div>
</template>
<script>
import api from "@/utils/api";

export default {
  props: {
    initialBrands: {
      type: Array,
      default: Array,
    },
    queryParameters: {
      type: Object,
      default: Object,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    brandOptions: [],
    brands: [],
    openedNodes: [],
    timeout: null,
    brand: {},
  }),

  watch: {
    brands(newValue, oldValue) {
      const differenceAdded = newValue.filter((x) => !oldValue.includes(x));
      const differenceRemoved = oldValue.filter((x) => !newValue.includes(x));

      if (
        this.checkForParent(differenceAdded).length !== 0 &&
        differenceAdded.length === 1
      ) {
        this.calculateSelectedItems(newValue, "added", differenceAdded[0]);
      } else if (
        this.checkForParent(differenceRemoved).length !== 0 &&
        differenceRemoved.length === 1
      ) {
        this.calculateSelectedItems(newValue, "removed", differenceRemoved[0]);
      } else {
        this.$emit("change", newValue);
      }
    },
  },

  computed: {
    isAllSelected() {
      return (
        this.getAllSelectableUuids().length > 0 &&
        this.getAllSelectableUuids().every((uuid) => this.brands.includes(uuid))
      );
    },
  },

  created() {
    this.loadBrands(true).then((resp) => {
      if (this.initialBrands.length !== 0) {
        this.brands = this.initialBrands;
        this.openedNodes = resp.map((brand) => brand.uuid);
      }
    });
  },

  methods: {
    getAllSelectableUuids() {
      return this.brandOptions.flatMap((brand) => [
        brand.uuid,
        ...((brand.children && brand.children.map((child) => child.uuid)) ||
          []),
      ]);
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.brands = [];
      } else {
        const allUuids = this.getAllSelectableUuids();
        this.brands = [...allUuids];
        this.openedNodes = this.brandOptions.map((brand) => brand.uuid);
      }
    },

    checkForParent(brands) {
      const parents = this.findParents();
      return parents.filter((parent) => brands.includes(parent.uuid));
    },
    findParents() {
      return this.brandOptions.filter(
        (element) => element.children.length !== 0
      );
    },

    findParent(parentUuid) {
      return (
        this.brandOptions.find((element) => element.uuid === parentUuid) || {}
      );
    },

    findChildren(parentUuid) {
      if (typeof parentUuid === "undefined") {
        return [];
      }

      const wantedParent = this.findParent(parentUuid);

      if (typeof wantedParent === "undefined") {
        return [];
      }

      return wantedParent.children;
    },

    getGroupType(item) {
      if (item.is_supergroup)
        return this.$t(
          "components.selectors.brandTreeViewSelector.organization"
        );
      if (item.is_range)
        return this.$t("components.selectors.brandTreeViewSelector.range");

      return this.$t("components.selectors.brandTreeViewSelector.brand");
    },

    calculateSelectedItems(brands, status, mutatedParent) {
      const parent = this.findParent(mutatedParent);
      const children = this.findChildren(mutatedParent);

      this.openedNodes = [...new Set([...this.openedNodes, parent.uuid])];

      if (status === "added") {
        this.brands = [
          ...new Set([
            ...brands,
            ...parent.children.map((child) => child.uuid),
          ]),
        ];
      }

      if (status === "removed") {
        this.brands = this.brands.filter(
          (brand) => !children.map((child) => child.uuid).includes(brand)
        );
      }

      // this.$emit("change", brands);
    },

    loadBrands(initalLoad = false) {
      const groupBy = (items, key) =>
        items.reduce(
          (result, item) => ({
            ...result,
            [item[key]]: [...(result[item[key]] || []), item],
          }),
          {}
        );

      const hierarchy = (data) => {
        //Grouping up the data on partner uuid
        const groupedDataByParentUuid = groupBy(data, "parent_uuid");

        //Find all brands that can be roots (supergroup or range cannot be roots in our case)
        let findBrands = Object.keys(groupedDataByParentUuid)
          .map((key) => {
            return groupedDataByParentUuid[key].filter((item) => {
              return !item.is_range && !item.is_supergroup;
            });
          })
          .filter((item) => {
            return item.length;
          })
          .flatMap((item) => {
            return item;
          });

        //CAUTION: We can build tree like this because data structure has only 3 layers (supergroup which is deleted, brand and range)
        const tree = findBrands.flatMap((item) => {
          return {
            ...item,
            children: groupedDataByParentUuid[item.uuid] || [],
          };
        });

        return tree;
      };

      let fetchBrands = () => {
        return new Promise((resolve, reject) => {
          this.brandSearch = this.brandSearch ? this.brandSearch : "";

          api
            .groupsAutocomplete({
              search: this.brandSearch,
              include_all_brands: true,
              ...this.queryParameters,
            })
            .then((resp) => {
              const removedSuperGroups = resp.data.filter((item) => {
                return !item.is_supergroup;
              });
              this.brandOptions = hierarchy(removedSuperGroups);
              resolve(resp.data);
            });
        });
      };

      if (initalLoad) return fetchBrands();

      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        return fetchBrands();
      }, 300); // delay
    },
  },
};
</script>
